/**
 * Live feed from Fastify API. In dev, Vite proxies `/v1` → `server`.
 * Public marketing site uses `?lang=EN` for canonical English wire copy.
 * B2B customers use their API key with `?lang=DA|NB|SV` (customer `target_lang`).
 */

/** Public site always requests English canonical content from the API. */
export const PUBLIC_SITE_FEED_LANG = "EN";

/**
 * API origin for `/v1/*` calls.
 * - Dev: empty → relative `/v1/...` so Vite proxies to Fastify.
 * - Prod: `VITE_PITCH_API_URL` from build, or same tab origin (fixes stale bundles / missing bake-in on deploy).
 */
export function resolvePitchApiBase() {
  const fromEnv = String(import.meta.env.VITE_PITCH_API_URL ?? "")
    .trim()
    .replace(/\/+$/, "");
  if (fromEnv) return fromEnv;
  if (import.meta.env.DEV) return "";
  if (typeof window !== "undefined" && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, "");
  }
  return "";
}

/** @param {Response} res */
async function readJsonBody(res) {
  const text = await res.text();
  const trimmed = text.trimStart();
  if (trimmed.startsWith("<") || trimmed.startsWith("<!")) {
    throw new Error(
      "API returned HTML instead of JSON. The request hit the marketing site instead of the Fastify API. On Cloudflare Pages, set runtime PITCH_API_ORIGIN to the Node API origin (not this hostname) and leave VITE_PITCH_API_URL empty so /v1 stays same-origin. Then reload.",
    );
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error(`Invalid JSON from feed: ${msg}`);
  }
}

function buildLangQuery(locale) {
  const loc = String(locale ?? "")
    .trim()
    .toUpperCase();
  if (loc === "EN" || loc === "DA" || loc === "NB" || loc === "SV") {
    return `?lang=${encodeURIComponent(loc)}`;
  }
  return "";
}

/**
 * @param {{ signal?: AbortSignal, locale?: string }} [opts]
 * `locale` defaults to EN for the public marketing site.
 */
export async function fetchPitchFeedJson(opts = {}) {
  const base = resolvePitchApiBase();
  const key = import.meta.env.VITE_PITCH_API_KEY;
  if (!key) {
    throw new Error("Missing VITE_PITCH_API_KEY in .env");
  }
  const loc = String(opts.locale ?? PUBLIC_SITE_FEED_LANG)
    .trim()
    .toUpperCase();
  const url = `${base}/v1/feed${buildLangQuery(loc)}`;
  let res;
  try {
    res = await fetch(url, {
      headers: { "X-Api-Key": key },
      signal: opts.signal,
      cache: "no-store",
    });
  } catch (e) {
    const name = e?.name;
    const msg = String(e?.message ?? e);
    if (name === "AbortError") throw e;
    if (
      name === "TypeError" &&
      (msg.includes("Failed to fetch") || msg.includes("NetworkError"))
    ) {
      throw new Error(
        "Cannot reach API — start the backend (`cd server` then `npm start`, port 3000) so Vite can proxy /v1/feed.",
      );
    }
    throw e;
  }
  if (!res.ok) {
    const t = await res.text();
    let detail = t.trim() ? t.slice(0, 200) : "(empty response)";
    try {
      const j = JSON.parse(t);
      if (j?.message) detail = String(j.message);
      else if (j?.error) detail = String(j.error);
    } catch {
      /* keep raw */
    }
    if (res.status === 502 || res.status === 504) {
      throw new Error(
        `Feed ${res.status}: Bad gateway — is the API running on port 3000? ${detail}`,
      );
    }
    throw new Error(`Feed ${res.status}: ${detail}`);
  }
  return readJsonBody(res);
}

/**
 * @param {string} articleId
 * @param {{ signal?: AbortSignal, locale?: string }} [opts]
 */
export async function fetchPitchFeedArticle(articleId, opts = {}) {
  const base = resolvePitchApiBase();
  const key = import.meta.env.VITE_PITCH_API_KEY;
  if (!key) {
    throw new Error("Missing VITE_PITCH_API_KEY in .env");
  }
  const path = `/v1/articles/${encodeURIComponent(String(articleId ?? ""))}`;
  const loc = String(opts.locale ?? PUBLIC_SITE_FEED_LANG)
    .trim()
    .toUpperCase();
  const url = `${base}${path}${buildLangQuery(loc)}`;
  let res;
  try {
    res = await fetch(url, {
      headers: { "X-Api-Key": key },
      signal: opts.signal,
      cache: "no-store",
    });
  } catch (e) {
    const name = e?.name;
    const msg = String(e?.message ?? e);
    if (name === "AbortError") throw e;
    if (
      name === "TypeError" &&
      (msg.includes("Failed to fetch") || msg.includes("NetworkError"))
    ) {
      throw new Error(
        "Cannot reach API — start the backend (`cd server` then `npm start`, port 3000).",
      );
    }
    throw e;
  }
  if (!res.ok) {
    const t = await res.text();
    let detail = t.trim() ? t.slice(0, 200) : "(empty response)";
    try {
      const j = JSON.parse(t);
      if (j?.message) detail = String(j.message);
      else if (j?.error) detail = String(j.error);
    } catch {
      /* keep raw */
    }
    throw new Error(`Article ${res.status}: ${detail}`);
  }
  return readJsonBody(res);
}